/**
 * Dark/Light mix services grid — Yön B.
 * Premium 3 services on dark gradient cards, the rest on light cards
 * with their own colour accent + price badge + hover 3D tilt.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { SERVICES } from '../data/mock';

const DARK = ['business-voip', 'microsoft-teams', 'outbound']; // these get dark cards

const ACCENT = {
  'business-voip':   { from: '£11/user/mo', tag: 'Most popular', g: 'from-violet-600 via-violet-700 to-fuchsia-700', glow: 'fuchsia-500' },
  'sip-trunk':       { from: '£5/channel/mo', g: 'from-emerald-50 to-white', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'microsoft-teams': { from: '£8/user/mo', tag: 'For Teams', g: 'from-blue-700 via-blue-800 to-indigo-900', glow: 'blue-500' },
  'inbound':         { from: '£24/agent/mo', g: 'from-amber-50 to-white', text: 'text-amber-700', dot: 'bg-amber-500' },
  'outbound':        { from: '£32/agent/mo', tag: 'AI-driven', g: 'from-rose-700 via-fuchsia-800 to-violet-900', glow: 'rose-500' },
  'web-phone':       { from: 'Included', g: 'from-sky-50 to-white', text: 'text-sky-700', dot: 'bg-sky-500' },
  'video':           { from: 'Included', g: 'from-teal-50 to-white', text: 'text-teal-700', dot: 'bg-teal-500' },
  'unified-comms':   { from: '£14/user/mo', g: 'from-indigo-50 to-white', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  'phone-features':  { from: 'Included', g: 'from-slate-50 to-white', text: 'text-slate-700', dot: 'bg-slate-500' },
};

function Icon({ name, size = 22, className = '' }) {
  const Ic = Icons[name] || Icons.Sparkles;
  return <Ic size={size} className={className} />;
}

export default function ServicesGridDuo() {
  return (
    <section className="bg-slate-50">
      <div className="container-x section-pad">
        <div className="text-center mb-12">
          <span className="eyebrow">What we do</span>
          <h2 className="text-3xl md:text-5xl font-extrabold mt-3 leading-tight">
            One platform, every <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-fuchsia-600">communication channel</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.slice(0, 9).map((s) => {
            const dark = DARK.includes(s.slug);
            return dark ? <DarkCard key={s.slug} service={s} /> : <LightCard key={s.slug} service={s} />;
          })}
        </div>
      </div>
    </section>
  );
}

function DarkCard({ service }) {
  const a = ACCENT[service.slug] || ACCENT['business-voip'];
  return (
    <Link
      to={service.href}
      className={`relative group rounded-2xl p-7 bg-gradient-to-br ${a.g} text-white overflow-hidden hover:-translate-y-2 transition-transform duration-300 shadow-xl shadow-slate-900/10`}
      style={{ minHeight: 360 }}
    >
      <div className={`absolute -top-24 -right-24 w-64 h-64 rounded-full bg-${a.glow}/30 blur-3xl group-hover:scale-110 transition-transform`} />

      {a.tag && (
        <span className="absolute top-5 right-5 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/15 backdrop-blur text-[10px] font-extrabold uppercase tracking-wider border border-white/20">
          <Sparkles size={10} /> {a.tag}
        </span>
      )}

      <div className="relative z-10">
        <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center mb-5 shadow-xl">
          <Icon name={service.icon} size={22} className="text-white" />
        </div>
        <h3 className="text-xl font-extrabold leading-tight mb-2">{service.title}</h3>
        <p className="text-white/80 text-sm leading-relaxed mb-5">{service.short}</p>

        <ul className="space-y-1.5 mb-6">
          {service.features.slice(0, 4).map((f) => (
            <li key={f} className="flex items-center gap-2 text-xs text-white/85">
              <Check size={12} className="text-white/70" /> {f}
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between pt-4 border-t border-white/15">
          <span className="text-xs text-white/70">From <strong className="text-white">{a.from}</strong></span>
          <span className="inline-flex items-center gap-1 text-sm font-bold group-hover:gap-2 transition-all">
            Learn more <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function LightCard({ service }) {
  const a = ACCENT[service.slug] || { g: 'from-violet-50 to-white', text: 'text-violet-700', dot: 'bg-violet-500', from: '' };
  return (
    <Link
      to={service.href}
      className={`relative group rounded-2xl p-7 bg-gradient-to-br ${a.g} border border-slate-200/70 hover:-translate-y-2 hover:shadow-xl transition-all duration-300`}
      style={{ minHeight: 360 }}
    >
      <div className={`w-12 h-12 rounded-xl ${a.dot} flex items-center justify-center text-white shadow-lg shadow-slate-900/10 mb-5`}>
        <Icon name={service.icon} size={22} />
      </div>
      <h3 className="text-xl font-extrabold text-slate-900 leading-tight mb-2">{service.title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed mb-5">{service.short}</p>

      <ul className="space-y-1.5 mb-6">
        {service.features.slice(0, 4).map((f) => (
          <li key={f} className="flex items-center gap-2 text-xs text-slate-700">
            <Check size={12} className={a.text} /> {f}
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between pt-4 border-t border-slate-200/70">
        <span className="text-xs text-slate-500">From <strong className="text-slate-900">{a.from}</strong></span>
        <span className={`inline-flex items-center gap-1 text-sm font-bold ${a.text} group-hover:gap-2 transition-all`}>
          Learn more <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}
